import React, { useCallback, useEffect, useRef, useState } from 'react';
import { PhotoIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useImagemProduto } from '../../hooks/useConferencias';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { focarScanBar } from './ScanBar';

interface FotoProdutoProps {
  /** Código do produto principal; null (item sem cadastro interno) mostra só o placeholder. */
  codigo: string | null;
  /** Nome do produto — vai no alt e no título da foto ampliada. */
  descricao: string;
  /** Lado da miniatura em px. Mobile usa maior (dedo + distância do olho na doca). */
  tamanho: number;
}

/**
 * Miniatura da foto do produto no checklist, com ampliação ao toque.
 *
 * <p>Existe pra o conferente confirmar, sem ler o código, que o volume na mão é o item da
 * linha — dois produtos com descrição quase idêntica (bitolas, cores) são o erro clássico
 * da doca.</p>
 *
 * <p>Produto sem foto cadastrada é o caso COMUM, não um erro: o placeholder é discreto e
 * não clicável, e o hook não repete o 404 (só indisponibilidade temporária é retentada —
 * ver `useImagemProduto`).</p>
 *
 * <p>A foto é consultada NO MEIO da conferência, então abrir/fechar a ampliação é caminho
 * quente: ao fechar, o foco volta pra barra de bipagem (`focarScanBar`). Sem isso o botão
 * desmonta, o foco cai no `body` e o próximo bipe é digitado no vazio — sem toast, sem erro,
 * sem contagem.</p>
 */
const FotoProduto: React.FC<FotoProdutoProps> = ({ codigo, descricao, tamanho }) => {
  const [ampliada, setAmpliada] = useState(false);
  const { data: url, isError } = useImagemProduto(codigo);
  const dialogRef = useRef<HTMLDivElement>(null);

  const fechar = useCallback(() => {
    setAmpliada(false);
    focarScanBar();
  }, []);

  // Trap de foco + Esc pelo hook padrão do projeto (useFocusTrap já trata Escape — não
  // duplicar o listener de janela do FotosConferenciaDialog aqui, seriam dois handlers pro
  // mesmo Esc). O travamento de scroll abaixo segue aquele dialog, esse não é do hook.
  useFocusTrap(dialogRef, ampliada, fechar);

  useEffect(() => {
    if (!ampliada) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = original;
    };
  }, [ampliada]);

  const estilo = { width: tamanho, height: tamanho };

  if (!url || isError) {
    return (
      <div
        style={estilo}
        aria-hidden="true"
        className="flex flex-shrink-0 items-center justify-center rounded-iris-sm border border-line bg-surface-2"
      >
        <PhotoIcon className="h-1/2 w-1/2 text-tx-3 opacity-40" />
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setAmpliada(true)}
        style={estilo}
        aria-label={`Ampliar foto de ${descricao}`}
        className="flex-shrink-0 overflow-hidden rounded-iris-sm border border-line bg-surface-2 transition-colors hover:border-accent focus:outline-none focus:ring-[3px] focus:ring-accent-soft"
      >
        <img src={url} alt={descricao} className="h-full w-full object-contain" />
      </button>

      {ampliada && (
        <div className="fixed inset-0 z-50" onClick={fechar}>
          <div className="fixed inset-0 bg-[var(--scrim)] backdrop-blur-[2px]" />

          <div className="fixed inset-0 flex flex-col items-center justify-center p-4">
            <div
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-label={`Foto de ${descricao}`}
              className="relative flex max-h-full w-full max-w-lg flex-col overflow-hidden rounded-iris-lg border border-line-strong bg-surface shadow-iris-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
                <p className="min-w-0 truncate text-sm font-medium text-tx" title={descricao}>
                  {descricao}
                </p>
                <button
                  type="button"
                  onClick={fechar}
                  aria-label="Fechar"
                  className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-iris text-tx-3 transition-colors hover:bg-surface-2 hover:text-tx md:h-8 md:w-8"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>
              <img src={url} alt={descricao} className="max-h-[70vh] w-full bg-surface-2 object-contain" />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FotoProduto;
